import React, { useState } from "react";
import { Combobox } from "@headlessui/react";
import { FaChevronDown } from "react-icons/fa";
import { Controller } from "react-hook-form";
import Label from "./label";
import { cn } from "../../utility/utils";

const CreatableSelect = ({
  control,
  name,
  rules,
  label,
  options = [],
  customKey = { value: "id", label: "name" },
  placeholder = "Select or type category...",
}) => {
  const [query, setQuery] = useState("");

  const valueKey = customKey.value;
  const labelKey = customKey.label;

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) =>
          option[labelKey]
            ?.toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        // If the value is a number/existing ID, we find its label.
        // Otherwise, if value is a custom string, it represents the expenses_type_name.
        let displayVal = "";
        const selectedOption = options.find((opt) => String(opt[valueKey]) === String(value));
        if (selectedOption) {
          displayVal = selectedOption[labelKey];
        } else if (value && isNaN(value)) {
          // If value is a non-numeric string, it is the custom typed category name
          displayVal = value;
        }

        const handleSelect = (val) => {
          // Set field value
          onChange(val);
          setQuery("");
        };

        return (
          <div className="flex flex-col space-y-2 relative w-full">
            <Label>
              {label}
              {rules?.required && <span className="text-red-600">*</span>}
            </Label>

            <Combobox value={value || ""} onChange={handleSelect}>
              <div className="relative w-full">
                <div className="relative w-full cursor-default overflow-hidden rounded-md border border-input bg-transparent text-sm shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring">
                  <Combobox.Input
                    className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:outline-none focus:ring-0 bg-transparent"
                    placeholder={placeholder}
                    displayValue={() => displayVal}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <FaChevronDown className="h-3 w-3 text-gray-400" aria-hidden="true" />
                  </Combobox.Button>
                </div>

                <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-[1500] border border-gray-200">
                  {filteredOptions.map((opt) => (
                    <Combobox.Option
                      key={opt[valueKey]}
                      className={({ active }) =>
                        cn(
                          "relative cursor-default select-none py-2 pl-3 pr-9",
                          active ? "bg-gray-100 text-gray-900" : "text-gray-900"
                        )
                      }
                      value={opt[valueKey]}
                    >
                      {opt[labelKey]}
                    </Combobox.Option>
                  ))}

                  {query.trim() !== "" && !options.some((opt) => opt[labelKey]?.toLowerCase() === query.trim().toLowerCase()) && (
                    <Combobox.Option
                      className={({ active }) =>
                        cn(
                          "relative cursor-pointer select-none py-2 pl-3 pr-9 bg-blue-50 text-blue-900 font-semibold",
                          active ? "bg-blue-100" : ""
                        )
                      }
                      value={query.trim()}
                    >
                      Create "{query.trim()}"
                    </Combobox.Option>
                  )}

                  {filteredOptions.length === 0 && query.trim() === "" && (
                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                      No categories found.
                    </div>
                  )}
                </Combobox.Options>
              </div>
            </Combobox>

            {error && (
              <small className="text-red-600 font-medium block pt-1 text-sm">
                {error.message}
              </small>
            )}
          </div>
        );
      }}
    />
  );
};

export default CreatableSelect;
